import { Box, CssBaseline, ThemeProvider, useMediaQuery } from "@mui/material";
import { useCallback, useMemo, useState, type FunctionComponent } from "react";
import FeatureNavigator from "./components/FeatureNavigator";
import GraphContainer from "./components/GraphContainer";
import Sidebar from "./components/Sidebar";
import type SelectedFeature from "./features/SelectedFeature";
import { buildTheme } from "./theme";

const App: FunctionComponent = () => {
  // Check if user prefers dark mode
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

  const [isSidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [selectedFeature, setSelectedFeature] =
    useState<SelectedFeature | null>("add-nodes");
  const [isDarkMode, setDarkMode] = useState<boolean>(prefersDarkMode);
  const [isTooltipsVisible, setTooltipsVisible] = useState<boolean>(true);

  const toggleSidebar = useCallback((): void => {
    setSidebarOpen(!isSidebarOpen);
  }, [isSidebarOpen]);

  const toggleFeature = useCallback((feature: SelectedFeature | null): void => {
    setSelectedFeature(feature);
  }, []);

  const toggleDarkMode = useCallback((): void => {
    setDarkMode(!isDarkMode);
  }, [isDarkMode]);

  const toggleTooltips = useCallback((): void => {
    setTooltipsVisible((prev) => !prev);
  }, []);

  const theme = useMemo(
    () => buildTheme(isDarkMode, isTooltipsVisible),
    [isDarkMode, isTooltipsVisible],
  );

  return (
    <ThemeProvider theme={theme}>
      <Box display="flex" height="100vh" width="100vw">
        <CssBaseline />

        {/* Sidebar */}
        <Sidebar isSidebarOpen={isSidebarOpen} onToggleSidebar={toggleSidebar}>
          <FeatureNavigator
            isSidebarOpen={isSidebarOpen}
            selectedItem={selectedFeature}
            onItemClick={toggleFeature}
          />
        </Sidebar>

        {/* Graph container */}
        <Box component="main" flexGrow={1}>
          <GraphContainer
            isSidebarOpen={isSidebarOpen}
            onToggleSidebar={toggleSidebar}
            selectedFeature={selectedFeature}
            onSelectFeature={toggleFeature}
            isDarkMode={isDarkMode}
            onToggleDarkMode={toggleDarkMode}
            isTooltipsVisible={isTooltipsVisible}
            onToggleTooltips={toggleTooltips}
          />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default App;
